import { useState, useRef } from "react";
import styles from "./TinkModal.module.css";
import useTranslator from "../../hooks/useTranslator";

// ─── Icons ────────────────────────────────────────────────────
const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ChevronLeft = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const UserIcon = () => (
  <svg viewBox="0 0 20 20" role="presentation">
    <path d="M13.7,10.72A6,6,0,1,0,10,12a8,8,0,0,1,8,8h2A10,10,0,0,0,13.7,10.72ZM10,10a4,4,0,1,1,4-4A4,4,0,0,1,10,10Z" />
    <path d="M4,14.66,2.5,13.34A10.15,10.15,0,0,0,0,20H10V18H2.26A8,8,0,0,1,4,14.66Z" />
  </svg>
);
const LockIcon = () => (
  <svg viewBox="0 0 20 20" role="presentation">
    <path d="M10,2a4,4,0,0,1,4,4h2A6,6,0,0,0,4,6H6A4,4,0,0,1,10,2Z" />
    <path d="M15,7H5a3,3,0,0,0-3,3v7a3,3,0,0,0,3,3H15a3,3,0,0,0,3-3V10A3,3,0,0,0,15,7Zm1,10a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1V10A1,1,0,0,1,5,9H15a1,1,0,0,1,1,1Z" />
    <polygon points="9 13.59 7.71 12.29 6.29 13.71 9 16.41 13.71 11.71 12.29 10.29 9 13.59" />
  </svg>
);
const PhoneIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.5" />
  </svg>
);
const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const CardIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);
const SavingsIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

// ─── Bank Data ────────────────────────────────────────────────
const BANKS_COMMON = [
  { id: 1, name: "Banco Sabadell", abbr: "BS", color: "var(--ink)" },
  { id: 2, name: "Banco Santander", abbr: "SAN", color: "var(--danger)" },
  { id: 3, name: "BBVA", abbr: "BBVA", color: "var(--ink)" },
  { id: 4, name: "CaixaBank", abbr: "CX", color: "var(--ink)" },
  { id: 5, name: "ING", abbr: "ING", color: "var(--orange)" },
  { id: 6, name: "Revolut", abbr: "R", color: "var(--ink)" },
];

const BANKS_OTHER = [
  { id: 7, name: "Abanca", abbr: "AB", color: "var(--ink)" },
  { id: 8, name: "Banco Cooperativo", abbr: "BC", color: "var(--success)" },
  { id: 9, name: "Banco Pichincha", abbr: "BP", color: "var(--warning)" },
  { id: 10, name: "Bankinter", abbr: "BK", color: "var(--orange)" },
  { id: 11, name: "Caixa Popular", abbr: "CP", color: "var(--success)" },
  {
    id: 12,
    name: "Caixa Rural Les Coves",
    abbr: "CR",
    color: "var(--success)",
  },
  { id: 13, name: "Caixa Vinaros", abbr: "CV", color: "var(--danger-text)" },
  { id: 14, name: "Caja Rural", abbr: "CR", color: "var(--success)" },
  { id: 15, name: "Caja Viva", abbr: "CV", color: "var(--success)" },
  { id: 16, name: "Cajamar", abbr: "CM", color: "var(--ink)" },
  { id: 17, name: "CajaSiete", abbr: "C7", color: "var(--success)" },
  { id: 18, name: "CRC", abbr: "CRC", color: "var(--success)" },
  { id: 19, name: "Global Caja", abbr: "GC", color: "var(--success)" },
  { id: 20, name: "IberCaja", abbr: "IC", color: "var(--danger-text)" },
  { id: 21, name: "Imagin", abbr: "IM", color: "var(--text-muted)" },
  { id: 22, name: "Kutxabank", abbr: "KX", color: "var(--ink)" },
  { id: 23, name: "Laboral Kutxa", abbr: "LK", color: "var(--ink)" },
  { id: 24, name: "N26", abbr: "N26", color: "var(--ink)" },
  { id: 25, name: "Openbank", abbr: "OB", color: "var(--danger)" },
  { id: 26, name: "RuralNostra", abbr: "RN", color: "var(--success)" },
  { id: 27, name: "Ruralvia", abbr: "RV", color: "var(--success)" },
  { id: 28, name: "Unicaja", abbr: "UC", color: "var(--success)" },
  {
    id: 29,
    name: "Wise",
    abbr: "W",
    color: "var(--lime)",
    textColor: "#155827",
  },
];

const ALL_BANKS = [...BANKS_COMMON, ...BANKS_OTHER];

export default function TinkModal({ onClose, onSuccess }) {
  // Steps: 1=intro, 2=bank select, 3=open bank app, 4=success
  const [step, setStep] = useState(1);
  const [selectedBank, setSelectedBank] = useState(null);
  const [search, setSearch] = useState("");
  const [refreshLoading, setRefreshLoading] = useState(false);
  const { _t } = useTranslator();
  const overlayRef = useRef(null);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const goTo = (n) => setStep(n);

  const goBack = () => {
    if (step === 3) {
      goTo(2);
      return;
    }
    if (step === 2) {
      goTo(1);
      return;
    }
  };

  const handleSelectBank = (bank) => {
    setSelectedBank(bank);
    goTo(3);
  };

  // Simulate refresh → connection success
  const handleRefresh = () => {
    setRefreshLoading(true);
    setTimeout(() => {
      setRefreshLoading(false);
      goTo(4);
    }, 2000);
  };

  const handleDone = () => {
    onSuccess?.();
    onClose();
  };

  const filteredBanks = ALL_BANKS.filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase())
  );
  const isSearching = search.trim().length > 0;
  const commonBanks = isSearching
    ? filteredBanks.filter((b) => b.id <= 6)
    : BANKS_COMMON;
  const otherBanks = isSearching
    ? filteredBanks.filter((b) => b.id > 6)
    : BANKS_OTHER;

  const getStepClass = (n) => {
    if (n === step) return `${styles.step} ${styles.stepActive}`;
    return `${styles.step} ${styles.stepExit}`;
  };

  return (
    <div
      className={styles.overlay}
      ref={overlayRef}
      onClick={handleOverlayClick}
    >
      <div className={styles.modal}>
        {/* Back */}
        {step > 1 && step < 4 && (
          <button
            className={styles.backBtn}
            onClick={goBack}
            aria-label="Go back"
          >
            <ChevronLeft />
          </button>
        )}

        {/* Close */}
        {step !== 4 && (
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <XIcon />
          </button>
        )}

        {/* Tink logo (steps 2+) */}
        {step >= 2 && (
          <div className={styles.tinkLogo}>
            <span className={styles.tinkLogoText}>tink</span>
            <span className={styles.tinkLogoSup}>↗</span>
            <span className={styles.tinkLogoSub}>{_t("aVisaSolution")}</span>
          </div>
        )}

        {/* ── Steps ── */}
        <div className={styles.stepsContainer}>
          {/* Step 1 – Intro */}
          <div className={getStepClass(1)}>
            <h1 className={styles.introTitle}>
              {_t("skorUsesTinkTo")}
              <br />
              {_t("connectToYourBank")}
            </h1>
            <div className={styles.introItems}>
              <div className={styles.introItem}>
                <UserIcon />
                <div>
                  <p className={styles.introLabel}>{_t("aboutYourData")}</p>
                  <p className={styles.introDesc}>
                    {_t("skorWillGainAccessToYourFinancialData")}{" "}
                    <a href="#">{_t("learnHowYourDataIsUsed")}</a>
                  </p>
                </div>
              </div>
              <div className={styles.introItem}>
                <LockIcon />
                <div>
                  <p className={styles.introLabel}>{_t("termsAndPrivacy")}</p>
                  <p className={styles.introDesc}>
                    {_t("bySelectingContinueYouAgreeToTinks")}{" "}
                    <a href="#">{_t("endUserTermsOfService")}</a> {_t("and")}{" "}
                    <a href="#">{_t("privacyPolicy")}</a>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 – Bank Select */}
          <div className={`${getStepClass(2)} ${styles.stepBank}`}>
            <h2 className={styles.stepHeading}>{_t("selectYourBank")}</h2>
            <label className={styles.searchLabel}>{_t("searchBank")}</label>
            <div className={styles.searchWrap}>
              <SearchIcon />
              <input
                className={styles.searchInput}
                type="text"
                placeholder={`${ALL_BANKS.length} banks`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className={styles.bankScrollArea}>
              {commonBanks.length > 0 && (
                <>
                  <p className={styles.bankSectionLabel}>{_t("mostCommon")}</p>
                  <div className={styles.bankGrid}>
                    {commonBanks.map((bank) => (
                      <div
                        key={bank.id}
                        className={`${styles.bankCard} ${selectedBank?.id === bank.id ? styles.bankCardSelected : ""}`}
                        onClick={() => handleSelectBank(bank)}
                      >
                        <div
                          className={styles.bankLogoCircle}
                          style={{ background: bank.color }}
                        >
                          <span style={{ color: bank.textColor || "#fff" }}>
                            {bank.abbr}
                          </span>
                        </div>
                        <span className={styles.bankCardName}>{bank.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {otherBanks.length > 0 && (
                <>
                  <p
                    className={styles.bankSectionLabel}
                    style={{ marginTop: "1.2vw" }}
                  >
                    {_t("allOtherBanks")}
                  </p>
                  <div className={styles.bankGrid}>
                    {otherBanks.map((bank) => (
                      <div
                        key={bank.id}
                        className={`${styles.bankCard} ${selectedBank?.id === bank.id ? styles.bankCardSelected : ""}`}
                        onClick={() => handleSelectBank(bank)}
                      >
                        <div
                          className={styles.bankLogoCircle}
                          style={{ background: bank.color }}
                        >
                          <span style={{ color: bank.textColor || "#fff" }}>
                            {bank.abbr}
                          </span>
                        </div>
                        <span className={styles.bankCardName}>{bank.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {filteredBanks.length === 0 && (
                <p className={styles.noResults}>
                  {_t("noBanksFoundFor", { search })}
                </p>
              )}
            </div>
          </div>

          {/* Step 3 – Open Bank App */}
          <div className={`${getStepClass(3)} ${styles.stepAppConnect}`}>
            <div className={styles.phoneIconWrap}>
              <div className={styles.phoneIconCircle}>
                <PhoneIcon />
              </div>
            </div>

            <h2 className={styles.appConnectTitle}>
              {_t("openYourBankAppToEnableSkorToConnect")}
            </h2>
            <p className={styles.appConnectDesc}>
              {_t(
                "weveSentARequestToYourBankOpenYourBankingAppAndApproveTheConnectionRequestFromTink"
              )}
            </p>

            {/* Bank badge */}
            <div className={styles.bankBadgeLight}>
              <div
                className={styles.bankBadgeLightLogo}
                style={{ background: selectedBank?.color || "#1a2e28" }}
              >
                <span
                  style={{
                    color: selectedBank?.textColor || "#fff",
                    fontSize: "var(--text-11)",
                    fontWeight: 700,
                  }}
                >
                  {selectedBank?.abbr || "TD"}
                </span>
              </div>
              <div>
                <p className={styles.bankBadgeLightLabel}>{_t("bank")}</p>
                <p className={styles.bankBadgeLightName}>
                  {selectedBank?.name || "Tink Demo Bank"}
                </p>
              </div>
            </div>

            {/* Steps */}
            <div className={styles.appSteps}>
              <div className={styles.appStepRow}>
                <div className={styles.appStepNum}>1</div>
                <p className={styles.appStepText}>
                  {_t("openYourBanksMobileAppOnYourPhone")}
                </p>
              </div>
              <div className={styles.appStepRow}>
                <div className={styles.appStepNum}>2</div>
                <p className={styles.appStepText}>
                  {_t("findThePendingConnectionRequestFromTinkSkor")}
                </p>
              </div>
              <div className={styles.appStepRow}>
                <div className={styles.appStepNum}>3</div>
                <p className={styles.appStepText}>
                  {_t("approveTheRequestThenTapRefreshBelow")}
                </p>
              </div>
            </div>

            {/* Waiting dots */}
            {!refreshLoading && (
              <div className={styles.waitingDots}>
                <span className={styles.dot} />
                <span className={`${styles.dot} ${styles.dot2}`} />
                <span className={`${styles.dot} ${styles.dot3}`} />
              </div>
            )}

            {refreshLoading && <div className={styles.refreshSpinner} />}
          </div>

          {/* Step 4 – Success */}
          <div className={`${getStepClass(4)} ${styles.stepSuccess}`}>
            <div className={styles.successIconWrap}>
              <div className={styles.successCircle}>
                <CheckIcon />
              </div>
            </div>
            <h2 className={styles.successTitle}>
              {_t("connectionSuccessful")}
            </h2>
            <p className={styles.successDesc}>
              {_t("yourBankAccountIsNowLinkedToSkor")}
            </p>
          </div>
        </div>
        {/* /stepsContainer */}

        {/* Footer */}
        <div className={styles.footer}>
          {step === 1 && (
            <button className={styles.btnPrimary} onClick={() => goTo(2)}>
              {_t("continue")}
            </button>
          )}
          {step === 2 && null}
          {step === 3 && (
            <button
              className={styles.btnRefresh}
              onClick={handleRefresh}
              disabled={refreshLoading}
            >
              {refreshLoading ? "Checking…" : "Refresh"}
            </button>
          )}
          {step === 4 && (
            <button className={styles.btnPrimary} onClick={handleDone}>
              {_t("done")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
